// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseInputSearch, BaseSelectFileModal, Loading } from '~/components/common';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { downloadFile } from '~/thunks/file/fileThunk';
import { quoteActions } from '~/thunks/quote/quoteSlice';
import { IListDataResponse } from '~/utils/interface/base';
import { TypeQuoteDetailEnum } from '~/utils/enum/common';
import { IQueryParamQuoteDetail } from '~/utils/interface/quotes';
import { getDetailQuote, updateQuote } from '~/thunks/quote/quoteThunk';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IBaseFile, IBodyUpdate, IFileItem } from '~/utils/interface/common';
import {
  EMPTY_STRING,
  DEFAULT_MAX_PAGE,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
} from '~/utils/constants/common';
import { convertToRelativeTime, createFormData, formatFileSize, getFileNameFromUri } from '~/utils/helper/common';
// Styles, images, icons
import styles from './DocumentsQuoteTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const DocumentsQuoteTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: quoteId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshQuoteDetail } = useAppSelector((store) => store.quote);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dataDocument, setDataDocument] = useState<IFileItem[]>([]);

  const [showAddFileModal, setShowAddFileModal] = useState<boolean>(false);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IQueryParamQuoteDetail>({
    quoteId: quoteId || EMPTY_STRING,
    type: TypeQuoteDetailEnum.DOCUMENTS,
    query: {
      page: DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_MAX_PAGE,
    },
  });
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetDetailQuoteDocument(paramObject);
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshQuoteDetail) return;

    handleGetDetailQuoteDocument(paramObject);

    dispatch(quoteActions.setRefreshQuoteDetail(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshQuoteDetail]);

  useEffect(() => {
    setParamObject({
      quoteId: quoteId || EMPTY_STRING,
      type: TypeQuoteDetailEnum.DOCUMENTS,
      query: {
        page: DEFAULT_CURRENT_PAGE,
        limit: DEFAULT_MAX_PAGE,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailQuoteDocument = (params: IQueryParamQuoteDetail) => {
    setIsLoading(true);

    dispatch(getDetailQuote(params))
      .unwrap()
      .then((res) => {
        const { responses: listDocument } = res.data as IListDataResponse<IFileItem[]>;

        if (!listDocument) return;

        setDataDocument(listDocument);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddFile = async (files: IBaseFile[]) => {
    const dataConvert = {
      quoteFile: files.map(({ id, ...resData }) => resData),
    };

    const formData = await createFormData(dataConvert, ['quoteFile']);

    const params: IBodyUpdate<FormData> = {
      id: quoteId || EMPTY_STRING,
      body: formData,
    };

    setIsLoading(true);
    dispatch(updateQuote(params))
      .unwrap()
      .then((_res) => {
        dispatch(quoteActions.setRefreshQuoteDetail(true));
        handleShowAddFileModal();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderItemDocument = (item: IFileItem, index: number) => {
    return (
      <Stack key={index} className={cx('itemDocument')}>
        <Stack gap={4} direction={'row'} alignItems={'center'}>
          <Box className={cx('bgIcon')}>
            <img src={icons.commonIconFileDocument} alt={t('altImg', { ns: 'common/base' })} />
          </Box>

          <Box>
            <Typography className={cx('label')}>{item.name}</Typography>
            <Typography className={cx('value')}>{` ${formatFileSize(Number(item?.size))} - ${t(
              'quote_detail_title_uploaded',
              { time: convertToRelativeTime(item.createdAt) }
            )}`}</Typography>
          </Box>
        </Stack>

        <Stack direction={'row'} gap={2}>
          <Button variant='outlined' className={cx('buttonView')} onClick={() => handleViewDocument(item)}>
            {t('table_action_view', { ns: 'common/base' })}
          </Button>

          <Button variant='outlined' className={cx('buttonDownload')} onClick={() => handleDownloadDocument(item)}>
            {t('common_text_download', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>
    );
  };

  const handleViewDocument = (item: IFileItem) => {
    window.open(item.url);
  };

  const handleDownloadDocument = (item: IFileItem) => {
    setIsLoading(true);

    dispatch(downloadFile(item.id))
      .unwrap()
      .then((response) => {
        const data: ArrayBuffer = response.data;

        if (data) {
          const blob = new Blob([data], { type: item.mimetype });

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = getFileNameFromUri(item.name);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          window.URL.revokeObjectURL(url);
        }
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleShowAddFileModal = () => {
    setShowAddFileModal(!showAddFileModal);
  };
  //#endregion Handle Function

  return (
    <Stack id='documentsQuoteTabComponent' gap={6} className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <Typography className={cx('textHeader')}>{t('quote_detail_title_documentation_attachments')}</Typography>

        <Stack gap={5} direction={'row'}>
          <BaseInputSearch
            width={200}
            typeStyle='outlined'
            onChange={handleSearchChange}
            borderRadius={100}
            placeholder={t('quote_detail_title_search_file')}
          />

          <Box>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleShowAddFileModal}
              startIcon={<img src={icons.commonIconUploadFileWhite} alt={'common_alt_img'} className={cx('icon')} />}
            >
              {t('quote_detail_title_upload_files')}
            </Button>
          </Box>
        </Stack>
      </Stack>

      <Stack gap={4}>
        {dataDocument && dataDocument.length > 0 ? (
          dataDocument.map((item, index) => {
            return renderItemDocument(item, index);
          })
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        )}
      </Stack>

      {isLoading && <Loading />}

      {showAddFileModal && <BaseSelectFileModal onAdd={handleAddFile} onClose={handleShowAddFileModal} />}
    </Stack>
  );
};

export default DocumentsQuoteTab;
